import React from "react";
import "../../styles/searchable-dropdown.css";

const normalize = (value) => String(value || "").trim().toLowerCase();

const SearchableDropdown = ({
  id,
  value,
  options = [],
  placeholder = "Search...",
  disabled = false,
  className = "",
  clearOnFocus = false,
  onChange,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const rootRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const filteredOptions = React.useMemo(() => {
    const query = normalize(value);
    if (!query) return options;
    return options.filter((opt) => normalize(opt.label).includes(query));
  }, [options, value]);

  React.useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const commitValue = (nextValue, option = null) => {
    if (typeof onChange === "function") onChange(nextValue, option);
  };

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    setIsOpen(true);
    setHighlightedIndex(-1);
    commitValue(nextValue, null);
  };

  const handleFocus = () => {
    if (disabled) return;
    setIsOpen(true);
    if (clearOnFocus) {
      commitValue("", null);
      requestAnimationFrame(() => {
        if (inputRef.current) inputRef.current.select();
      });
    }
  };

  const handleSelectOption = (option) => {
    commitValue(option.label, option);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (event) => {
    if (!isOpen && event.key === "ArrowDown") {
      setIsOpen(true);
      return;
    }

    if (!isOpen || filteredOptions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : 0,
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredOptions.length - 1,
      );
    } else if (event.key === "Enter") {
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        event.preventDefault();
        handleSelectOption(filteredOptions[highlightedIndex]);
      }
    } else if (event.key === "Escape") {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  return (
    <div className="searchable-dropdown" ref={rootRef}>
      <input
        id={id}
        ref={inputRef}
        className={`searchable-dropdown-input ${className}`.trim()}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        autoComplete="off"
      />

      {isOpen && !disabled && (
        <div className="searchable-dropdown-menu" role="listbox">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <button
                key={option.value}
                type="button"
                className={`searchable-dropdown-option ${index === highlightedIndex ? "active" : ""}`.trim()}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelectOption(option)}
              >
                {option.label}
              </button>
            ))
          ) : (
            <div className="searchable-dropdown-empty">No matching results</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
